import React, { useState, useRef } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { ImageMajor } from '@shopify/polaris-icons';
import { Icon } from '@shopify/polaris';

import useIntersectionObserver from '../../utils/useIntersectionObserver';
import Breakpoints from '../../design-utils/breakpoints';
import { FinalFocusRef } from '../SlidePanel/SlidePanel';

type LazyImageProps = {
  image: string | null;
  thumbnail: string | null;
  alt: string;
  onClick?: (finalFocusRef: FinalFocusRef) => void;
  onKeyDown?: (e: React.KeyboardEvent, finalFocusRef: FinalFocusRef) => void;
};

type ImageProps = {
  isLoaded: boolean;
};

type ContainerProps = {
  padding: number;
  isEmpty: boolean;
};

const Thumbnail = styled.img((props: ImageProps) => ({
  opacity: props.isLoaded ? 0 : 1,
}));

const Image = styled.img((props: ImageProps) => ({
  opacity: props.isLoaded ? 1 : 0,
}));

const Container = styled.div<ContainerProps>`
  background-color: ${(props) => (props.isEmpty ? '#fafafa' : 'transparent')};
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  padding-bottom: ${(props) => `${props.padding * 100}%`};

  @media screen and (max-width: ${Breakpoints.md}) {
    width: 220px;
    padding-bottom: ${(props) => `${props.padding * 100}%`};
  }
`;

const LazyImage = ({
  image,
  thumbnail,
  alt,
  onClick,
  onKeyDown,
}: LazyImageProps): JSX.Element => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [padding, setPadding] = useState(1);
  const imgRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLImageElement | null>(null);

  useIntersectionObserver(imgRef, () => {
    setIsInView(true);
  });

  const handleThumbOnLoad = () => {
    const width = thumbRef.current?.width || 1;
    const height = thumbRef.current?.height || 1;
    setPadding(height / width);
  };

  const handleOnLoad = () => {
    setIsLoaded(true);
  };

  return (
    <Container
      isEmpty={image == null}
      padding={padding}
      data-testid="lazy-container"
      className="bundle-lazy-image-container"
      style={{ display: 'block' }}
      ref={imgRef}
      role={onClick ? 'button' : 'img'}
      tabIndex={0}
      onKeyDown={(e) => onKeyDown(e, imgRef)}
      onClick={() => onClick(imgRef)}
      aria-label={alt}
    >
      {!image && (
        <div className={styles.icon} data-testid="lazy-icon" aria-label={alt}>
          <Icon source={ImageMajor} tone="base" />
        </div>
      )}

      {isInView && image && thumbnail && (
        <>
          <Thumbnail
            ref={thumbRef}
            data-testid="lazy-thumb"
            onLoad={handleThumbOnLoad}
            isLoaded={!!isLoaded}
            className={`${styles.base} ${styles.thumb}`}
            src={thumbnail}
            alt={alt}
            aria-hidden={isLoaded}
          />
          <Image
            data-testid="lazy-image"
            isLoaded={!!isLoaded}
            className={`${styles.base} ${styles.image}`}
            src={image}
            onLoad={handleOnLoad}
            alt={alt}
            aria-hidden={!isLoaded}
          />
        </>
      )}
    </Container>
  );
};

const styles = {
  icon: css`
    width: 64px;
    height: 64px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0.25;
  `,
  base: css`
    object-fit: contain;
    position: absolute;
    transition: opacity 1s ease;
    width: 100%;
  `,
  image: css`
    opacity: 0;
  `,
  thumb: css`
    opacity: 1;
    filter: blur(15px);
  `,
};
export default LazyImage;
